// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchPersonas, deletePersonaFunc, setPreferredPersona } from '../../store/personaSlice';
import { getMeUser } from '../../store/userSlice';
import { resetOnboarding } from '../../store/onboardingSlice';
import { setModalState, modalViewsConst } from '../../store/modalSlice';
import { RootState } from '../../store';
import { PERSONA_CONFIG } from '../../config/appConfig';
import { FiTrash2 } from 'react-icons/fi';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { Skeleton, SkeletonGroup } from '../Skeleton';
import SettingsRow from './SettingsRow';
import { ReactComponent as UsersIcon } from '../../assets/icons/users-01.svg';

interface ManageProfilesSectionProps {
  userId?: string;
}

const ManageProfilesSection: React.FC<ManageProfilesSectionProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const { personas, loading } = useSelector((state: RootState) => state.personas);
  const preferredPersonaId = useSelector(
    (state: RootState) =>
      (state.user.permissionDetails as { preferredPersonaId?: string })?.preferredPersonaId
  );
  const modalState = useSelector((state: RootState) => state.modalState);

  useEffect(() => {
    if (isExpanded) {
      dispatch(fetchPersonas() as any);
    }
  }, [isExpanded, dispatch]);

  // Refetch personas when delete modal closes with success
  useEffect(() => {
    if (
      modalState.lastResult?.success &&
      modalState.lastResult?.type === "persona" &&
      !modalState.modalOpen
    ) {
      dispatch(fetchPersonas() as any);
    }
  }, [modalState.modalOpen, modalState.lastResult, dispatch]);

  const personaList = Object.values(personas);
  const isAtLimit = personaList.length >= PERSONA_CONFIG.MAX_PERSONAS;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCreatePersona = () => {
    if (isAtLimit) {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.notification,
          properties: {
            __modalSize: "sm",
            title: 'Profile Limit Reached',
            message: `You can only create up to ${PERSONA_CONFIG.MAX_PERSONAS} profiles.`,
          },
        })
      );
      return;
    }
    
    dispatch(resetOnboarding());
    navigate('/onboarding-v2');
  };

  const handleDeletePersona = (persona: any) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.deletePersona,
        properties: { __modalSize: "sm", id: persona.id, name: persona.name },
      })
    );
  };

  const handleSetPreferred = async (personaId: string) => {
    if (!userId) return;
    
    try {
      await dispatch(setPreferredPersona({ userId, personaId }) as any);
      // Refresh user data to get the updated preferredPersonaId
      await dispatch(getMeUser(userId) as any);
    } catch (error) {
      console.error('Error setting preferred persona:', error);
    }
  };


  // Facts read flat — a pill would say "interactive", and these are read-only
  const facts = (persona: any) => {
    const out: Array<{ label: string; value: string }> = [];
    let role: string | undefined;
    try {
      role = persona.summary ? JSON.parse(persona.summary)?.role : undefined;
    } catch {
      role = undefined;
    }
    if (role) out.push({ label: "Role", value: role });
    if (persona.markets?.length) {
      out.push({
        label: "Markets",
        value:
          persona.markets.slice(0, 2).join(", ") +
          (persona.markets.length > 2 ? ` +${persona.markets.length - 2}` : ""),
      });
    }
    if (persona.regions?.length) out.push({ label: "Regions", value: persona.regions.join(", ") });
    if (persona.selectedNeeds?.length) {
      out.push({
        label: "Focus",
        value:
          persona.selectedNeeds.slice(0, 2).join(", ") +
          (persona.selectedNeeds.length > 2 ? ` +${persona.selectedNeeds.length - 2}` : ""),
      });
    }
    return out;
  };

  return (
    <SettingsRow
      title="Manage My Profiles"
      description={
        isExpanded
          ? `${personaList.length} of ${PERSONA_CONFIG.MAX_PERSONAS} profiles`
          : "Create and manage your personas"
      }
      icon={<UsersIcon aria-hidden className="h-4 w-4 [&_path]:stroke-current" />}
      onClick={handleToggleExpand}
      expanded={isExpanded}
    >
      {loading === "pending" ? (
        <SkeletonGroup count={2} label="Loading profiles" className="flex flex-col gap-3">
          <div className="eai-card p-4">
            <Skeleton className="h-5 w-40" radius="md" />
            <Skeleton className="mt-3 h-3.5 w-2/3" />
            <Skeleton className="mt-2 h-3.5 w-1/2" />
          </div>
        </SkeletonGroup>
      ) : personaList.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-8 text-center">
          <svg
            className="h-10 w-10 text-eai-ink-ghost"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <p className="eai-card-title mt-3">No profiles yet</p>
          <p className="eai-description-sm mt-1">Get started by creating your first profile.</p>
          <button type="button" onClick={handleCreatePersona} className="eai-btn-cta mt-5">
            Create Profile
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {personaList.map((persona: any) => {
            const isPreferred = preferredPersonaId === persona.id;
            const selectable = persona.completed && !isPreferred;
            return (
              <div
                key={persona.id}
                onClick={() => selectable && handleSetPreferred(persona.id)}
                className={`group p-4 ${selectable ? "eai-card-interactive cursor-pointer" : "eai-card"} ${
                  isPreferred ? "border-eai-success-border bg-eai-success-surface-soft" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <h4 className="eai-card-title min-w-0 truncate">
                        {persona.name || "Untitled Persona"}
                      </h4>
                      {isPreferred ? (
                        <span className="eai-tag-pill-sm shrink-0 border-eai-success-border bg-eai-success-surface text-eai-success-text-strong">
                          Active
                        </span>
                      ) : persona.completed ? (
                        <span className="eai-tag-pill-sm shrink-0">Inactive</span>
                      ) : (
                        <span className="eai-tag-pill-sm shrink-0 text-eai-warning-text">
                          Incomplete
                        </span>
                      )}
                    </div>

                    {facts(persona).length > 0 && (
                      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                        {facts(persona).map(({ label, value }) => (
                          <div key={label} className="min-w-0">
                            <span className="eai-meta-label">{label}</span>
                            <p className="truncate font-inter text-eai-body-sm text-eai-ink">
                              {value}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {persona.description && (
                      <p className="eai-description-sm mt-3 line-clamp-2">{persona.description}</p>
                    )}

                    <p className="mt-2 font-inter text-eai-body-xs text-eai-ink-faint">
                      Created {new Date(persona.createdAt || Date.now()).toLocaleDateString()}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePersona(persona);
                    }}
                    className="eai-btn-icon eai-btn-icon-danger eai-card-reveal shrink-0"
                    title="Delete profile"
                    aria-label={`Delete ${persona.name || "profile"}`}
                  >
                    <FiTrash2 size={12} aria-hidden />
                  </button>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleCreatePersona}
            disabled={isAtLimit}
            className="flex w-full items-center justify-center gap-2 rounded-eai-card border border-dashed border-eai-border py-3 font-outfit text-eai-control text-eai-ink-muted transition-colors hover:border-eai-border-hover hover:bg-eai-surface-hover disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          >
            <PlusIcon aria-hidden className="h-4 w-4 [&_path]:stroke-current" />
            {isAtLimit
              ? `Profile limit reached (${PERSONA_CONFIG.MAX_PERSONAS})`
              : "Add New Profile"}
          </button>
        </div>
      )}
    </SettingsRow>
  );
};

export default ManageProfilesSection;
